import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2,
  LogOut,
  Wallet,
  Receipt,
  RefreshCw,
  MapPin,
  ChevronDown,
  ArrowRight,
  CreditCard,
  LifeBuoy,
  Truck,
  Plus,
  Package,
  DollarSign,
  Settings2,
  HelpCircle,
  CheckCircle2,
  Building2,
  Bell,
  X,
  Mail,
  Pencil,
} from "lucide-react";
import { DASHBOARD } from "@/constants/testIds";
import { useLang } from "@/lib/useLang";
import { PortalHeader, PortalFooter } from "@/components/PortalChrome";
import PayInvoiceModal from "@/components/PayInvoiceModal";
import AutopayModal from "@/components/AutopayModal";
import ConfirmDetailsModal from "@/components/ConfirmDetailsModal";
import { authApi, paymentApi, portalApi } from "@/lib/api";

const SUPPORT_CATEGORY_ORDER = [
  "missed_pickup",
  "extra_pickup",
  "container_issue",
  "billing_question",
  "account_service_change",
  "other",
];

const CATEGORY_ICON = {
  missed_pickup: Truck,
  extra_pickup: Plus,
  container_issue: Package,
  billing_question: DollarSign,
  account_service_change: Settings2,
  other: HelpCircle,
};

function fmtCurrency(v, lang) {
  try {
    return new Intl.NumberFormat(lang === "es" ? "es-US" : "en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(Number(v) || 0);
  } catch {
    return `$${(Number(v) || 0).toFixed(2)}`;
  }
}
// Date-only strings from Zoho (e.g. "2025-07-12") must be treated as calendar
// dates — NOT as UTC midnight — otherwise browsers in negative UTC offsets
// (all of the US) render the previous day. For strings with an explicit time
// component we keep the browser's default parsing (local time).
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
function parseCalendarDate(iso) {
  if (!iso) return null;
  if (typeof iso === "string" && DATE_ONLY_RE.test(iso)) {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? null : dt;
}
function fmtDate(iso, lang) {
  if (!iso) return "—";
  const dt = parseCalendarDate(iso);
  if (!dt) return iso;
  try {
    return new Intl.DateTimeFormat(lang === "es" ? "es-US" : "en-US", {
      dateStyle: "medium",
    }).format(dt);
  } catch {
    return iso;
  }
}
function fmtDateTime(iso, lang) {
  if (!iso) return null;
  const dt = parseCalendarDate(iso);
  if (!dt) return iso;
  try {
    return new Intl.DateTimeFormat(lang === "es" ? "es-US" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(dt);
  } catch {
    return iso;
  }
}
// Title-case a value for display — capitalise the first letter of every
// word/hyphenated segment. Preserves already-uppercase acronyms (MSW, ACH).
function fmtTitle(v) {
  if (v === null || v === undefined) return "—";
  const s = String(v).trim();
  if (!s) return "—";
  return s
    .split(/(\s+|-)/)
    .map((tok) => {
      if (/^\s+$/.test(tok) || tok === "-") return tok;
      // Fully-uppercase short tokens (MSW, ACH, USA) — leave as-is
      if (tok.length <= 4 && tok === tok.toUpperCase() && /[A-Z]/.test(tok)) return tok;
      return tok.charAt(0).toUpperCase() + tok.slice(1).toLowerCase();
    })
    .join("");
}

/* -------------------------------------------------------------------------- */
/*  Reusable little pieces                                                    */
/* -------------------------------------------------------------------------- */
function SummaryCard({ icon: Icon, label, valueSlot, footerSlot, testId, tone }) {
  const border = tone === "gold" ? "border-[#C8A46B]/40" : "border-[#EDE5D4]";
  return (
    <div
      data-testid={testId}
      className={`rounded-2xl bg-white ${border} border shadow-[0_4px_24px_rgba(11,25,48,0.04)] p-5 sm:p-6 flex flex-col gap-4`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#4B5563]">
          {label}
        </span>
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F0E9DC]">
          <Icon className="h-4.5 w-4.5 text-[#0B1930]" strokeWidth={1.75} />
        </span>
      </div>
      <div className="flex-1">{valueSlot}</div>
      {footerSlot && <div>{footerSlot}</div>}
    </div>
  );
}

function StatusPill({ status, t }) {
  const map = t.dashboard.accountStatusValues || {};
  const label = map[status] || status;
  const tone =
    status === "active"
      ? "bg-[#DDF3E4] text-[#0F5132]"
      : status === "past_due"
        ? "bg-[#FDE8E4] text-[#8A2A16]"
        : "bg-[#EDE5D4] text-[#0B1930]";
  return (
    <span
      data-testid={DASHBOARD.accountStatus}
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${tone}`}
    >
      {label}
    </span>
  );
}

function InvoiceStatusChip({ status, t }) {
  const map = t.dashboard.invoiceStatusValues || {};
  const label = map[status] || status;
  const tone =
    status === "paid"
      ? "bg-[#DDF3E4] text-[#0F5132]"
      : status === "past_due"
        ? "bg-[#FDE8E4] text-[#8A2A16]"
        : "bg-[#F0E9DC] text-[#0B1930]";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${tone}`}
    >
      {label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Selector (account + location)                                             */
/* -------------------------------------------------------------------------- */
function Selector({ label, value, options, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [open]);
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full border border-[#EDE5D4] bg-white px-4 py-2 text-sm font-medium text-[#0B1930] hover:bg-[#F5F1EA] transition-colors max-w-[280px]"
        data-testid={testId}
      >
        <span className="truncate">{current?.label || label}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[#4B5563]" />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 min-w-[240px] rounded-2xl border border-[#EDE5D4] bg-white shadow-[0_16px_48px_rgba(11,25,48,0.12)] p-1.5">
          {options.map((o) => (
            <button
              type="button"
              key={o.value}
              onClick={() => {
                setOpen(false);
                onChange(o.value);
              }}
              className={`flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition-colors ${
                o.value === value
                  ? "bg-[#F0E9DC] text-[#0B1930] font-semibold"
                  : "text-[#0B1930] hover:bg-[#F5F1EA]"
              }`}
            >
              <span className="truncate">{o.label}</span>
              {o.value === value && (
                <CheckCircle2 className="ml-auto h-4 w-4 text-[#B8925A]" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */
export default function PortalDashboard() {
  const navigate = useNavigate();
  const { lang, t, toggle } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();

  const [accounts, setAccounts] = useState(null); // array | null
  const [selectedAccountId, setSelectedAccountId] = useState(
    searchParams.get("account") || null,
  );
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [error, setError] = useState("");
  const [signingOut, setSigningOut] = useState(false);
  const [payInvoice, setPayInvoice] = useState(null); // invoice being paid
  const [autopayOpen, setAutopayOpen] = useState(false);
  const [autopayBusy, setAutopayBusy] = useState(false);
  // Payment-terms reminder: shows on every fresh login/session; the X
  // dismisses it only until the browser session ends.
  const [reminderDismissed, setReminderDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("zmex_pay_reminder_dismissed_v1") === "1";
    } catch {
      return false;
    }
  });
  const dismissReminder = useCallback(() => {
    try {
      sessionStorage.setItem("zmex_pay_reminder_dismissed_v1", "1");
    } catch {
      /* ignore */
    }
    setReminderDismissed(true);
  }, []);

  // Contact & billing info — customer self-service edit state.
  const [contactEdit, setContactEdit] = useState(false);
  const [contactDraft, setContactDraft] = useState({
    phone: "",
    billing_email: "",
    mailing_address: "",
  });
  const [contactBusy, setContactBusy] = useState(false);
  const [contactFlash, setContactFlash] = useState(null); // {type, msg}

  // Opening the pay window dismisses the confirm-details popup so the two
  // can never overlap.
  const openPayModal = (inv) => {
    setConfirmSnoozed(true);
    setConfirmOpen(false);
    setPayInvoice(inv);
  };

  const contactInfo = dashboard?.customer_contact_info || {};

  // One-time "Confirm your details" popup: shows on login until the
  // customer saves (details_confirmed_at set). "Not now" snoozes only
  // until the next page load/login.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmSnoozed, setConfirmSnoozed] = useState(false);

  useEffect(() => {
    if (!dashboard) return;
    // Never auto-open while a payment window is up — the popup must never
    // cover or interfere with payment.
    if (payInvoice || autopayOpen) return;
    if (
      !dashboard.customer_contact_info?.details_confirmed_at &&
      !confirmSnoozed
    ) {
      setConfirmOpen(true);
    }
  }, [dashboard, confirmSnoozed, payInvoice, autopayOpen]);

  // Login reminder text: admin-editable announcement wins, else default.
  const ann = dashboard?.announcement;
  const reminderText =
    (ann && (lang === "es" ? ann.text_es || ann.text_en : ann.text_en)) ||
    t.dashboard.paymentReminderBody;

  const startContactEdit = () => {
    setContactDraft({
      phone: contactInfo.phone || "",
      billing_email: contactInfo.billing_email || "",
      mailing_address: contactInfo.mailing_address || "",
    });
    setContactFlash(null);
    setContactEdit(true);
  };

  const saveContactInfo = async () => {
    if (!selectedAccountId || contactBusy) return;
    setContactBusy(true);
    setContactFlash(null);
    try {
      const { data } = await portalApi.updateContactInfo(selectedAccountId, {
        phone: contactDraft.phone,
        billing_email: contactDraft.billing_email,
        mailing_address: contactDraft.mailing_address,
      });
      setDashboard((d) =>
        d ? { ...d, customer_contact_info: data.customer_contact_info } : d,
      );
      setContactEdit(false);
      setContactFlash({ type: "success", msg: t.dashboard.contactInfoSaved });
    } catch (err) {
      setContactFlash({
        type: "error",
        msg: err?.detail || "Unable to save. Please try again.",
      });
    } finally {
      setContactBusy(false);
    }
  };

  const refreshDashboard = useCallback(async () => {
    if (!selectedAccountId) return;
    try {
      const { data } = await portalApi.accountDashboard(selectedAccountId);
      setDashboard(data);
    } catch {
      /* keep old data */
    }
  }, [selectedAccountId]);

  const onDisableAutopay = useCallback(async () => {
    if (!selectedAccountId || autopayBusy) return;
    setAutopayBusy(true);
    try {
      await paymentApi.disableAutopay(selectedAccountId);
      await refreshDashboard();
    } catch (err) {
      setError(err?.detail || "Unable to turn off autopay.");
    } finally {
      setAutopayBusy(false);
    }
  }, [selectedAccountId, autopayBusy, refreshDashboard]);

  // Load account list on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await portalApi.accounts();
        if (cancelled) return;
        setAccounts(data.accounts || []);
        if (!selectedAccountId && (data.accounts || []).length === 1) {
          setSelectedAccountId(data.accounts[0].account_id);
        }
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 401) {
          navigate("/", { replace: true });
          return;
        }
        setError(err?.detail || "Unable to load your accounts right now.");
      } finally {
        if (!cancelled) setLoadingAccounts(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load dashboard when account changes.
  useEffect(() => {
    if (!selectedAccountId) {
      setDashboard(null);
      return undefined;
    }
    setLoadingDashboard(true);
    setError("");
    let cancelled = false;
    (async () => {
      try {
        const { data } = await portalApi.accountDashboard(selectedAccountId);
        if (cancelled) return;
        setDashboard(data);
        const firstLoc = data.service_locations?.[0]?.location_id || null;
        setSelectedLocationId((prev) =>
          prev && data.service_locations.some((l) => l.location_id === prev)
            ? prev
            : firstLoc,
        );
        // Reflect selection in the URL so refresh keeps the account.
        setSearchParams(
          (params) => {
            const next = new URLSearchParams(params);
            next.set("account", selectedAccountId);
            return next;
          },
          { replace: true },
        );
      } catch (err) {
        if (cancelled) return;
        if (err?.status === 401) {
          navigate("/", { replace: true });
          return;
        }
        setError(err?.detail || "Unable to load this account right now.");
      } finally {
        if (!cancelled) setLoadingDashboard(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  const currentLocation = useMemo(() => {
    if (!dashboard || !selectedLocationId) return null;
    return (
      dashboard.service_locations.find(
        (l) => l.location_id === selectedLocationId,
      ) || null
    );
  }, [dashboard, selectedLocationId]);

  const onSignOut = useCallback(async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    navigate("/", { replace: true });
  }, [navigate, signingOut]);

  // ------------------------------------------------------------------------
  // Loading / error / no-account states
  // ------------------------------------------------------------------------
  if (loadingAccounts) {
    return (
      <div
        className="min-h-screen flex flex-col zmex-page-bg"
        data-testid={DASHBOARD.page}
      >
        <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
        <main className="flex-1 flex items-center justify-center">
          <div
            className="flex items-center gap-3 text-[#4B5563]"
            data-testid={DASHBOARD.loading}
          >
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        </main>
        <PortalFooter t={t} />
      </div>
    );
  }

  if (error && !accounts?.length) {
    return (
      <div
        className="min-h-screen flex flex-col zmex-page-bg"
        data-testid={DASHBOARD.page}
      >
        <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
        <main className="flex-1 flex items-center justify-center px-4">
          <p className="text-center text-sm text-red-600 max-w-md">{error}</p>
        </main>
        <PortalFooter t={t} />
      </div>
    );
  }

  // Multi-account picker
  if (!selectedAccountId && (accounts?.length || 0) > 1) {
    return (
      <div
        className="min-h-screen flex flex-col zmex-page-bg"
        data-testid={DASHBOARD.page}
      >
        <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
        <main className="flex-1">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 sm:pt-20 pb-16">
            <h1 className="font-heading uppercase tracking-tight text-[#0B1930] text-3xl sm:text-4xl">
              {t.dashboard.pickAccountTitle}
            </h1>
            <p className="mt-3 text-[#4B5563] text-[15px] leading-relaxed">
              {t.dashboard.pickAccountBody}
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {accounts.map((a) => (
                <button
                  key={a.account_id}
                  type="button"
                  onClick={() => setSelectedAccountId(a.account_id)}
                  className="text-left rounded-2xl bg-white border border-[#EDE5D4] p-5 hover:border-[#C8A46B] hover:shadow-[0_10px_30px_rgba(11,25,48,0.08)] transition-all"
                  data-testid={DASHBOARD.accountPickerCard(a.account_id)}
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0E9DC]">
                      <Building2 className="h-5 w-5 text-[#0B1930]" />
                    </span>
                    <div>
                      <p className="font-heading text-lg text-[#0B1930]">
                        {a.display_name}
                      </p>
                      <p className="text-xs text-[#4B5563] mt-0.5">
                        {a.service_locations.length}{" "}
                        {a.service_locations.length === 1
                          ? "location"
                          : "locations"}
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <span className="text-[#4B5563]">
                      {t.dashboard.balance}:{" "}
                      <span className="font-semibold text-[#0B1930]">
                        {fmtCurrency(a.current_balance, lang)}
                      </span>
                    </span>
                    <ArrowRight className="h-4 w-4 text-[#B8925A]" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </main>
        <PortalFooter t={t} />
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div
        className="min-h-screen flex flex-col zmex-page-bg"
        data-testid={DASHBOARD.page}
      >
        <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
        <main className="flex-1 flex items-center justify-center">
          <div
            className="flex items-center gap-3 text-[#4B5563]"
            data-testid={DASHBOARD.loading}
          >
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        </main>
        <PortalFooter t={t} />
      </div>
    );
  }

  // ------------------------------------------------------------------------
  // Main dashboard
  // ------------------------------------------------------------------------
  const accountOptions = (accounts || []).map((a) => ({
    value: a.account_id,
    label: a.display_name,
  }));
  const locationOptions = (dashboard.service_locations || []).map((l) => ({
    value: l.location_id,
    label: l.label,
  }));

  return (
    <div
      className="min-h-screen flex flex-col zmex-page-bg"
      data-testid={DASHBOARD.page}
    >
      <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
      <main className="relative flex-1">
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 pt-8 sm:pt-12 pb-16">
          {/* Top selector row */}
          <div className="flex flex-wrap items-center gap-3">
            {accountOptions.length > 1 && (
              <Selector
                label={t.dashboard.selectAccount}
                value={selectedAccountId}
                options={accountOptions}
                onChange={setSelectedAccountId}
                testId={DASHBOARD.selectorAccount}
              />
            )}
            {locationOptions.length > 1 && (
              <Selector
                label={t.dashboard.selectLocation}
                value={selectedLocationId}
                options={locationOptions}
                onChange={setSelectedLocationId}
                testId={DASHBOARD.selectorLocation}
              />
            )}
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate("/portal/support")}
                className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[#EDE5D4] bg-white px-4 py-2 text-sm font-semibold text-[#0B1930] hover:bg-[#F5F1EA] transition-colors"
              >
                <LifeBuoy className="h-4 w-4" />
                {t.dashboard.getSupport}
              </button>
              <button
                type="button"
                onClick={onSignOut}
                disabled={signingOut}
                className="inline-flex items-center gap-2 rounded-full border border-[#EDE5D4] bg-white px-4 py-2 text-sm font-semibold text-[#0B1930] hover:bg-[#F5F1EA] transition-colors disabled:opacity-70"
                data-testid={DASHBOARD.signOutBtn}
              >
                {signingOut ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.dashboard.signingOut}
                  </>
                ) : (
                  <>
                    <LogOut className="h-4 w-4" />
                    {t.dashboard.signOut}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Welcome */}
          <div className="mt-8">
            <p className="text-sm text-[#4B5563]">
              {t.dashboard.welcomeBack}
            </p>
            <h1
              className="mt-1 font-heading uppercase tracking-tight text-[#0B1930] text-3xl sm:text-4xl md:text-5xl"
              data-testid={DASHBOARD.greeting}
            >
              {dashboard.user_display_name || dashboard.display_name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[#4B5563]">
              <span
                className="font-semibold text-[#0B1930]"
                data-testid={DASHBOARD.accountName}
              >
                {dashboard.display_name}
              </span>
              {currentLocation?.address && (
                <span
                  className="flex items-center gap-1.5"
                  data-testid={DASHBOARD.serviceAddress}
                >
                  <MapPin className="h-3.5 w-3.5 text-[#B8925A]" />
                  {currentLocation.address}
                </span>
              )}
              <StatusPill status={dashboard.account_status} t={t} />
            </div>
          </div>

          {/* Payment-terms reminder — shown to every customer on login */}
          {!reminderDismissed && (
            <div
              className="mt-6 flex items-start gap-3 rounded-2xl border border-[#E3CE9F] bg-[#F7EFD9] px-5 py-4"
              data-testid={DASHBOARD.paymentReminder}
              role="alert"
            >
              <span
                className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#C8A46B]"
                aria-hidden="true"
              >
                <Bell className="h-4 w-4 text-[#0B1930]" />
              </span>
              <div className="flex-1">
                <p className="text-sm font-bold uppercase tracking-wide text-[#0B1930]">
                  {t.dashboard.paymentReminderTitle}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-[#4B5563]">
                  {reminderText}
                </p>
              </div>
              <button
                type="button"
                onClick={dismissReminder}
                aria-label={t.dashboard.paymentReminderDismiss}
                title={t.dashboard.paymentReminderDismiss}
                className="shrink-0 rounded-full p-1.5 text-[#4B5563] transition-colors hover:bg-[#E3CE9F] hover:text-[#0B1930]"
                data-testid={DASHBOARD.paymentReminderDismiss}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Summary cards */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            <SummaryCard
              label={t.dashboard.balance}
              icon={Wallet}
              tone="gold"
              testId={DASHBOARD.cardBalance}
              valueSlot={
                <p className="font-heading text-3xl text-[#0B1930]">
                  {fmtCurrency(dashboard.current_balance, lang)}
                </p>
              }
              footerSlot={
                dashboard.current_balance > 0 && dashboard.open_invoices.length > 0 && (
                  <button
                    type="button"
                    onClick={() => openPayModal(dashboard.open_invoices[0])}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A46B] hover:bg-[#B8925A] text-[#0B1930] px-4 py-2.5 text-sm font-bold uppercase tracking-wide transition-colors"
                    data-testid={DASHBOARD.ctaPayNow}
                  >
                    <CreditCard className="h-4 w-4" />
                    {t.dashboard.payNow}
                  </button>
                )
              }
            />

            <SummaryCard
              label={t.dashboard.nextDue}
              icon={Receipt}
              testId={DASHBOARD.cardNextDue}
              valueSlot={
                dashboard.next_payment_due ? (
                  <>
                    <p className="font-heading text-3xl text-[#0B1930]">
                      {fmtCurrency(
                        dashboard.next_payment_due.amount,
                        lang,
                      )}
                    </p>
                    <p className="mt-1 text-xs text-[#4B5563]">
                      {t.dashboard.dueOn}{" "}
                      {fmtDate(dashboard.next_payment_due.due_date, lang)}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-[#4B5563]">
                    {t.dashboard.nextDueEmpty}
                  </p>
                )
              }
            />

            <SummaryCard
              label={t.dashboard.invoicesOpen}
              icon={Receipt}
              testId={DASHBOARD.cardOpenInvoices}
              valueSlot={
                <p className="font-heading text-3xl text-[#0B1930]">
                  {dashboard.open_invoices_count}
                </p>
              }
              footerSlot={
                dashboard.open_invoices_count > 0 && (
                  <a
                    href="#billing"
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[#B8925A] hover:text-[#0B1930] transition-colors"
                    data-testid={DASHBOARD.ctaViewInvoices}
                  >
                    {t.dashboard.viewInvoices}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                )
              }
            />

            <SummaryCard
              label={t.dashboard.autopay}
              icon={RefreshCw}
              testId={DASHBOARD.cardAutopay}
              valueSlot={
                <p
                  className={`font-heading text-2xl ${
                    dashboard.autopay_enrolled
                      ? "text-[#0F5132]"
                      : "text-[#0B1930]"
                  }`}
                >
                  {dashboard.autopay_enrolled
                    ? t.dashboard.autopayOn
                    : t.dashboard.autopayOff}
                </p>
              }
              footerSlot={
                <button
                  type="button"
                  onClick={() =>
                    dashboard.autopay_enrolled
                      ? onDisableAutopay()
                      : setAutopayOpen(true)
                  }
                  disabled={autopayBusy}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[#B8925A] hover:text-[#0B1930] transition-colors disabled:opacity-60"
                  data-testid={
                    dashboard.autopay_enrolled
                      ? DASHBOARD.autopayDisableBtn
                      : DASHBOARD.autopaySetupBtn
                  }
                >
                  {autopayBusy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : null}
                  {dashboard.autopay_enrolled
                    ? "Turn off Autopay"
                    : t.dashboard.setUpAutopay}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              }
            />
          </div>

          {/* Billing */}
          <section
            id="billing"
            className="mt-10"
            data-testid={DASHBOARD.billingSection}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading uppercase tracking-wide text-[#0B1930] text-xl sm:text-2xl">
                {t.dashboard.billingHeading}
              </h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div
                className="rounded-2xl bg-white border border-[#EDE5D4] p-5 sm:p-6"
                data-testid={DASHBOARD.invoicesTable}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-[#0B1930]">
                    Invoices
                  </h3>
                  <span className="text-[11px] text-[#4B5563]">
                    {(dashboard.all_invoices || []).length} total
                  </span>
                </div>
                {(dashboard.all_invoices || []).length === 0 && (
                  <p className="text-sm text-[#4B5563]">
                    {t.dashboard.invoicesEmpty}
                  </p>
                )}
                {(dashboard.all_invoices || []).length > 0 && (
                  <div className="divide-y divide-[#EDE5D4] max-h-96 overflow-y-auto pr-1">
                    {(dashboard.all_invoices || []).map((inv) => (
                      <div
                        key={inv.invoice_id}
                        className="py-3 flex items-center gap-4"
                        data-testid={DASHBOARD.invoiceRow(inv.invoice_id)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#0B1930] truncate">
                            {inv.invoice_number}
                          </p>
                          <p className="text-xs text-[#4B5563] mt-0.5">
                            {inv.status === "paid" ? "Paid" : t.dashboard.invoiceDueDate}{" "}
                            {fmtDate(inv.status === "paid" ? (inv.last_payment_date || inv.invoice_date) : inv.due_date, lang)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-semibold text-[#0B1930]">
                            {fmtCurrency(
                              inv.status === "paid"
                                ? inv.total || inv.amount
                                : inv.remaining_balance,
                              lang,
                            )}
                          </p>
                          <div className="mt-1 flex items-center gap-2 justify-end">
                            <InvoiceStatusChip status={inv.status} t={t} />
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}/api/portal/accounts/${selectedAccountId}/invoices/${inv.invoice_id}/pdf`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[11px] font-semibold text-[#B8925A] hover:text-[#0B1930] transition-colors"
                            >
                              {t.dashboard.invoiceView}
                            </a>
                            {inv.remaining_balance > 0 && inv.status !== "paid" && (
                              <button
                                type="button"
                                onClick={() => openPayModal(inv)}
                                className="inline-flex items-center gap-1 rounded-full bg-[#0B1930] hover:bg-[#111A2E] text-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors"
                                data-testid={DASHBOARD.payInvoiceBtn(inv.invoice_id)}
                              >
                                <CreditCard className="h-3 w-3" />
                                Pay
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div
                className="rounded-2xl bg-white border border-[#EDE5D4] p-5 sm:p-6"
                data-testid={DASHBOARD.paymentsTable}
              >
                <h3 className="text-sm font-semibold uppercase tracking-wide text-[#0B1930] mb-3">
                  {t.dashboard.billingTabPayments}
                </h3>
                {dashboard.recent_payments.length === 0 && (
                  <p className="text-sm text-[#4B5563]">
                    {t.dashboard.paymentsEmpty}
                  </p>
                )}
                {dashboard.recent_payments.length > 0 && (
                  <div className="divide-y divide-[#EDE5D4]">
                    {dashboard.recent_payments.map((p) => (
                      <div
                        key={p.payment_id}
                        className="py-3 flex items-center gap-4"
                        data-testid={DASHBOARD.paymentRow(p.payment_id)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-[#0B1930] truncate">
                            {p.method}
                          </p>
                          <p className="text-xs text-[#4B5563] mt-0.5">
                            {fmtDate(p.payment_date, lang)}
                            {p.invoice_number && ` • ${p.invoice_number}`}
                          </p>
                        </div>
                        <p className="text-sm font-semibold text-[#0F5132]">
                          {fmtCurrency(p.amount, lang)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Service info */}
          {currentLocation && (
            <section
              className="mt-10 rounded-2xl bg-white border border-[#EDE5D4] p-5 sm:p-7"
              data-testid={DASHBOARD.serviceInfoCard}
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0E9DC]">
                  <Truck className="h-5 w-5 text-[#0B1930]" />
                </span>
                <h2 className="font-heading uppercase tracking-wide text-[#0B1930] text-xl sm:text-2xl">
                  {t.dashboard.serviceInfo}
                </h2>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                    {t.dashboard.serviceAddress}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-[#0B1930]">
                    {currentLocation.address || "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                    {t.dashboard.containerSize}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-[#0B1930]">
                    {fmtTitle(currentLocation.container_size)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                    {t.dashboard.collectionFrequency}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-[#0B1930]">
                    {fmtTitle(currentLocation.collection_frequency)}
                  </dd>
                </div>
                {currentLocation.service_days &&
                  currentLocation.service_days.length > 0 && (
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                        Pickup Days
                      </dt>
                      <dd
                        className="mt-1 text-sm font-semibold text-[#0B1930]"
                        data-testid="dashboard-pickup-days"
                      >
                        {currentLocation.service_days
                          .map((d) => fmtTitle(d).slice(0, 3))
                          .join(" · ")}
                      </dd>
                    </div>
                  )}
                <div>
                  <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                    {t.dashboard.nextCollection}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-[#0B1930]">
                    {currentLocation.next_collection_at
                      ? fmtDateTime(currentLocation.next_collection_at, lang)
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                    {t.dashboard.serviceStatus}
                  </dt>
                  <dd className="mt-1 text-sm font-semibold text-[#0B1930]">
                    {fmtTitle(currentLocation.service_status)}
                  </dd>
                </div>
                {currentLocation.waste_stream && (
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                      Waste Stream
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-[#0B1930]" data-testid="dashboard-waste-stream">
                      {fmtTitle(currentLocation.waste_stream)}
                    </dd>
                  </div>
                )}
                {currentLocation.service_notes && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                      {t.dashboard.serviceNotes}
                    </dt>
                    <dd className="mt-1 text-sm text-[#0B1930]">
                      {currentLocation.service_notes}
                    </dd>
                  </div>
                )}
              </dl>
              <div className="mt-5 rounded-xl bg-[#F5F1EA] px-4 py-3 text-xs text-[#4B5563]">
                {t.dashboard.serviceChangeHint}
              </div>
            </section>
          )}

          {/* Contact & billing info — customer self-service */}
          <section
            className="mt-10 rounded-2xl bg-white border border-[#EDE5D4] p-5 sm:p-7"
            data-testid={DASHBOARD.contactInfoCard}
          >
            <div className="flex items-center justify-between gap-3 mb-1">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0E9DC]">
                  <Mail className="h-5 w-5 text-[#0B1930]" />
                </span>
                <h2 className="font-heading uppercase tracking-wide text-[#0B1930] text-xl sm:text-2xl">
                  {t.dashboard.contactInfoTitle}
                </h2>
              </div>
              {!contactEdit && (
                <button
                  type="button"
                  onClick={startContactEdit}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#EDE5D4] px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#0B1930] transition-colors hover:bg-[#F0E9DC]"
                  data-testid={DASHBOARD.contactInfoEditBtn}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  {t.dashboard.contactInfoEdit}
                </button>
              )}
            </div>
            <p className="mb-4 text-xs text-[#4B5563]">
              {t.dashboard.contactInfoHint}
            </p>

            {contactFlash && (
              <div
                className={`mb-4 rounded-xl px-4 py-2.5 text-sm flex items-start gap-2 ${
                  contactFlash.type === "success"
                    ? "bg-emerald-50 border border-emerald-200 text-emerald-900"
                    : "bg-red-50 border border-red-200 text-red-800"
                }`}
                data-testid={DASHBOARD.contactInfoFlash}
              >
                {contactFlash.type === "success" ? (
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                ) : (
                  <X className="h-4 w-4 mt-0.5 shrink-0" />
                )}
                <span>{contactFlash.msg}</span>
              </div>
            )}

            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
              {/* Service address — always read-only, office-only change */}
              <div>
                <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                  {t.dashboard.serviceAddress}
                </dt>
                <dd className="mt-1 text-sm font-semibold text-[#0B1930]">
                  {currentLocation?.address || "—"}
                </dd>
                <dd className="mt-1 text-[11px] text-[#9CA3AF]">
                  {t.dashboard.contactInfoAddressLocked}
                </dd>
              </div>

              <div>
                <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                  {t.dashboard.contactInfoPhone}
                </dt>
                <dd className="mt-1">
                  {contactEdit ? (
                    <input
                      type="tel"
                      value={contactDraft.phone}
                      onChange={(e) =>
                        setContactDraft((d) => ({ ...d, phone: e.target.value }))
                      }
                      placeholder="(407) 555-0101"
                      className="w-full rounded-full bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                      data-testid={DASHBOARD.contactInfoPhoneInput}
                    />
                  ) : (
                    <span className="text-sm font-semibold text-[#0B1930]">
                      {contactInfo.phone || t.dashboard.contactInfoNotProvided}
                    </span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                  {t.dashboard.contactInfoBillingEmail}
                </dt>
                <dd className="mt-1">
                  {contactEdit ? (
                    <input
                      type="email"
                      value={contactDraft.billing_email}
                      onChange={(e) =>
                        setContactDraft((d) => ({
                          ...d,
                          billing_email: e.target.value,
                        }))
                      }
                      placeholder="you@example.com"
                      className="w-full rounded-full bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                      data-testid={DASHBOARD.contactInfoEmailInput}
                    />
                  ) : (
                    <span className="text-sm font-semibold text-[#0B1930] break-all">
                      {contactInfo.billing_email ||
                        t.dashboard.contactInfoNotProvided}
                    </span>
                  )}
                </dd>
              </div>

              <div>
                <dt className="text-xs uppercase tracking-wide text-[#4B5563]">
                  {t.dashboard.contactInfoMailing}
                </dt>
                <dd className="mt-1">
                  {contactEdit ? (
                    <input
                      type="text"
                      value={contactDraft.mailing_address}
                      onChange={(e) =>
                        setContactDraft((d) => ({
                          ...d,
                          mailing_address: e.target.value,
                        }))
                      }
                      placeholder="Street, City, State ZIP"
                      maxLength={200}
                      className="w-full rounded-full bg-white border border-[#EDE5D4] px-3 py-2 text-sm text-[#0B1930] focus:outline-none focus:border-[#C8A46B]"
                      data-testid={DASHBOARD.contactInfoMailingInput}
                    />
                  ) : (
                    <span className="text-sm font-semibold text-[#0B1930]">
                      {contactInfo.mailing_address ||
                        t.dashboard.contactInfoNotProvided}
                    </span>
                  )}
                </dd>
              </div>
            </dl>

            {contactEdit && (
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setContactEdit(false);
                    setContactFlash(null);
                  }}
                  disabled={contactBusy}
                  className="rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wide text-[#0B1930] transition-colors hover:bg-[#EDE5D4] disabled:opacity-60"
                  data-testid={DASHBOARD.contactInfoCancelBtn}
                >
                  {t.dashboard.contactInfoCancel}
                </button>
                <button
                  type="button"
                  onClick={saveContactInfo}
                  disabled={contactBusy}
                  className="inline-flex items-center gap-2 rounded-full bg-[#0B1930] px-5 py-2 text-xs font-bold uppercase tracking-wide text-white transition-colors hover:bg-[#111A2E] disabled:opacity-60"
                  data-testid={DASHBOARD.contactInfoSaveBtn}
                >
                  {contactBusy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {contactBusy
                    ? t.dashboard.contactInfoSaving
                    : t.dashboard.contactInfoSave}
                </button>
              </div>
            )}
          </section>

          {/* Get support */}
          <section
            className="mt-12"
            data-testid={DASHBOARD.supportSection}
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0E9DC]">
                <LifeBuoy className="h-5 w-5 text-[#0B1930]" />
              </span>
              <div>
                <h2 className="font-heading uppercase tracking-wide text-[#0B1930] text-xl sm:text-2xl">
                  {t.dashboard.supportHeading}
                </h2>
                <p className="text-xs text-[#4B5563] mt-1">
                  {t.dashboard.supportBody}
                </p>
              </div>
            </div>
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {SUPPORT_CATEGORY_ORDER.map((key) => {
                const Icon = CATEGORY_ICON[key];
                const cat = t.dashboard.supportCategories[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      navigate(
                        `/portal/support?category=${key}&account=${selectedAccountId}${
                          selectedLocationId
                            ? `&location=${selectedLocationId}`
                            : ""
                        }`,
                      )
                    }
                    className="text-left rounded-2xl bg-white border border-[#EDE5D4] p-5 hover:border-[#C8A46B] hover:shadow-[0_10px_30px_rgba(11,25,48,0.08)] transition-all group"
                    data-testid={DASHBOARD.supportCategoryBtn(key)}
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F0E9DC] group-hover:bg-[#C8A46B]/25 transition-colors">
                        <Icon className="h-5 w-5 text-[#0B1930]" />
                      </span>
                      <div>
                        <p className="font-heading uppercase text-[15px] tracking-wide text-[#0B1930]">
                          {cat.title}
                        </p>
                        <p className="mt-1 text-xs text-[#4B5563] leading-relaxed">
                          {cat.body}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => navigate("/portal/support/requests")}
                className="text-sm font-semibold text-[#B8925A] hover:text-[#0B1930] transition-colors inline-flex items-center gap-1"
              >
                {t.support.listHeading}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </section>

          {loadingDashboard && (
            <div className="mt-6 flex items-center gap-2 text-xs text-[#4B5563]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Refreshing…
            </div>
          )}
        </div>
      </main>
      <PortalFooter t={t} />

      <PayInvoiceModal
        open={!!payInvoice}
        onClose={() => setPayInvoice(null)}
        accountId={selectedAccountId}
        invoice={payInvoice}
        onSuccess={refreshDashboard}
      />
      <AutopayModal
        open={autopayOpen}
        onClose={() => setAutopayOpen(false)}
        accountId={selectedAccountId}
        onSuccess={refreshDashboard}
      />
      {confirmOpen && dashboard && (
        <ConfirmDetailsModal
          accountId={selectedAccountId}
          serviceAddress={currentLocation?.address || ""}
          initial={dashboard.customer_contact_info}
          primaryPhone={dashboard.primary_phone}
          primaryEmail={dashboard.primary_email}
          t={t.dashboard}
          onSnooze={() => {
            setConfirmSnoozed(true);
            setConfirmOpen(false);
          }}
          onSaved={(info) => {
            setDashboard((d) =>
              d ? { ...d, customer_contact_info: info } : d,
            );
            setConfirmOpen(false);
            setContactFlash({
              type: "success",
              msg: t.dashboard.confirmSaved,
            });
          }}
        />
      )}
    </div>
  );
}
